import type { TableColumnCtx } from "element-plus"
import type { MathType } from "mathjs"
import type { TransformedItemOrderVOTable } from "./print"
import type { OrderItem } from "@/api/business/types/printing"
import type { ProductItemType } from "@/api/business/types/product"
import type { ItemOrderVOExt } from "@/api/business/types/table"
import type { MerchantCustomerUserDetailDTO } from "@/api/user/types/member"
import type { BaseTableColumns } from "@/components/table/types"
import type { PackageChildPrintItem } from "@/utils/packagePrintItems"
import { buildReceiptItemVariantKey } from "@repo/teashop-utils"
import { convertCentsToYuan, convertYuanToCents as convertYuanToCentsBase } from "@repo/utils"
import math from "@/plugins/mathjs"
import { getValueByPath } from "@/utils"
import { computedMinutes } from "@/utils/datetime"
import { findMemberDiscount, findMemberPrice } from "@/utils/memberLevelBenefit"

export { convertCentsToYuan }
// Element Plus 的 DefaultRow 类型定义
type DefaultRow = Record<PropertyKey, any>

export type CustomSummaryMethod<T extends DefaultRow = DefaultRow> = (
  _data: {
    columns: TableColumnCtx[]
    data: T[]
  },
  _customColumnList: BaseTableColumns<any>
) => string[]

/**
 * 将字符串转换为千分位格式
 * @param amount 字符串格式的数字
 * @returns 千分位格式的字符串
 */
export { formatToThousands } from "@repo/utils"

/**
 * 将人民币分转换为元，并保留两位小数
 * @param cents 人民币分
 * @returns 转换后的人民币元字符串，保留两位小数
 */
export function convertCentsToYuanAndSymbol(cents: number, isFormatToThousands = false, minPrecision = 2): string {
  return `￥${convertCentsToYuan(cents, isFormatToThousands, minPrecision)}`
}

/**
 * 转换元为分
 * @param number
 * @returns
 */
export const convertYuanToCents = convertYuanToCentsBase

export function convertYuanInputToCents(value: unknown): number {
  const yuan = Number(value || 0)
  if (!Number.isFinite(yuan)) return 0
  return Math.round(convertYuanToCentsBase(yuan))
}

/**
 * 获取大数计算结果的小数点位数
 * @param value - 要计算的小数值，可以是 string 或 number 类型
 * @param multiplier - 可选的乘数，如果不传入默认为 1
 * @returns 小数点后的位数
 */
function getDecimalPrecision(value: MathType): number {
  const resultStr = value.toString()
  const decimalIndex = resultStr.indexOf(".")
  return decimalIndex === -1 ? 0 : resultStr.length - decimalIndex - 1
}

/**
 * 折扣转换
 * @param rate 折扣比例，如0.5
 * @param zeroPrecision 在没有小数时的表现，为0则不要小数
 * @returns 口语上的折扣，如5.0折
 */
export function convertRateToDiscount(rate: number, minPrecision: number = 1, zeroPrecision: number = 0): string {
  const res = math.multiply(math.bignumber(rate), 10)
  const decimalPrecision = getDecimalPrecision(res)
  const precision = decimalPrecision == 0 ? zeroPrecision : decimalPrecision || minPrecision
  return math.format(res, { notation: "fixed", precision })
}

/**
 * 折扣转换
 * @param rate 折扣比例，如0.5
 * @returns 口语上的折扣，如50折
 */
export function convertRateToDiscountLabel(rate: number, minPrecision: number = 1): string {
  const res = math.multiply(math.bignumber(rate), 100)
  const decimalPrecision = getDecimalPrecision(res)
  return math.format(res, { notation: "fixed", precision: decimalPrecision || minPrecision })
}

/**
 * 将输入的折扣转换为折扣比例
 * @param discount 折扣，如95
 * @returns 折扣比例，如0.95
 */
export function convertInputDiscountToRate(discount: number): number {
  return parseFloat(math.divide(math.bignumber(discount), 100).toString())
}

/**
 * 将负数转换为正数
 */
export function convertNegativeToPositive(number: number): number {
  return Math.abs(number)
}

// TODO 通过type定义每一类的类型，根据类型自定决定累加方式，增加单元测试方法
export const getSummaries = <T extends DefaultRow = DefaultRow>(
  param: {
    columns: TableColumnCtx[]
    data: T[]
  },
  NoSumColumnList: string[],
  AmountColumnList: string[],
  TimeColumnList: string[],
  isCreditOperation?: boolean,
) => {
  const { columns, data } = param
  const sums: string[] = []

  columns.forEach((column: any, index: any) => {
    // if (column.type === "index") {
    //   sums[index] = "合计"
    //   return
    // }
    if (index === 0) {
      sums[index] = "合计"
      return
    }
    if (NoSumColumnList.includes(column.property) || !column.property) {
      sums[index] = "--"
      return
    }

    if (isCreditOperation && AmountColumnList.includes(column.property)) {
      const values = data
        .filter((item: any) => !item.isCreditOperation)
        .map((item: any) => Number(getValueByPath(item, column.property)))
      const tempSum = values.reduce((prev: any, curr: any) => {
        const value = Number(curr)
        if (!isNaN(value)) {
          return prev + curr
        } else {
          return prev
        }
      }, 0)
      sums[index] = convertCentsToYuan(tempSum, true)
      return
    }

    const values = data.map((item: any) => Number(getValueByPath(item, column.property)))
    const tempSum = values.reduce((prev: any, curr: any) => {
      const value = Number(curr)
      if (!isNaN(value)) {
        return prev + curr
      } else {
        return prev
      }
    }, 0)
    if (AmountColumnList.includes(column.property)) {
      sums[index] = convertCentsToYuan(tempSum, true)
      return
    }
    if (TimeColumnList.includes(column.property)) {
      sums[index] = computedMinutes(tempSum)
      return
    }
    sums[index] = tempSum
  })

  return sums
}

// TODO SummaryMethod<any>  是否可以将自定义的key放入的columns的项中
export const getSummariesWrapper: CustomSummaryMethod = ({ columns, data }, customColumnList) => {
  let NoSumColumnList: string[] = []
  let AmountColumnList: string[] = []
  let TimeColumnList: string[] = []
  if (customColumnList) {
    NoSumColumnList = customColumnList
      .filter((item) => {
        return ["noSum", "noSumAmount"].includes(item.customType || "")
      })
      .map((item) => String(item.prop))
    AmountColumnList = customColumnList
      .filter((item) => {
        return ["amount", "noSumAmount"].includes(item.customType || "")
      })
      .map((item) => String(item.prop))
    TimeColumnList = customColumnList
      .filter((item) => {
        return item.customType === "time"
      })
      .map((item) => String(item.prop))
  }

  const sums: string[] = []

  columns.forEach((column: any, index: any) => {
    // if (column.type === "index") {
    //   sums[index] = "合计"
    //   return
    // }
    const customSummary = customColumnList[index]?.customSummary
    if (customSummary) {
      sums[index] = customSummary(data)
      return
    }
    if (index === 0) {
      sums[index] = "合计"
      return
    }
    if (NoSumColumnList.includes(column.property ?? column.prop) || (!column.property && !column.prop)) {
      sums[index] = "--"
      return
    }
    const values = data.map((item: any) => Number(getValueByPath(item, column.property ?? column.prop)))
    const tempSum = values.reduce((prev: any, curr: any) => {
      const value = Number(curr)
      if (!isNaN(value)) {
        return prev + curr
      } else {
        return prev
      }
    }, 0)
    if (AmountColumnList.includes(column.property ?? column.property)) {
      sums[index] = convertCentsToYuan(tempSum, true)
      return
    }
    if (TimeColumnList.includes(column.property ?? column.property)) {
      sums[index] = computedMinutes(tempSum)
      return
    }
    sums[index] = tempSum
  })
  return sums
}

/**
 * 获取商品的折后价
 * @param product 商品信息
 * @param currentMember 当前会员信息
 * @param enabledMemberPrice 是否启用会员价
 * @returns 折后价
 */
export function getAfterSalePrice(
  product: Pick<ProductItemType, "salePrice" | "memberDiscountInfo">,
  currentMember: MerchantCustomerUserDetailDTO | null | undefined,
  enabledMemberPrice: number,
) {
  const memberDiscountInfo = product.memberDiscountInfo
  if (!currentMember || !memberDiscountInfo) {
    return product.salePrice
  }
  if (memberDiscountInfo.type === "memberDiscount") {
    const discount = findMemberDiscount(memberDiscountInfo, currentMember)?.discount
    if (discount != null) {
      return math.multiply(product.salePrice, discount)
    } else {
      return product.salePrice
    }
  } else if (memberDiscountInfo.type === "memberPrice" && enabledMemberPrice === 1) {
    const price = findMemberPrice(memberDiscountInfo, currentMember)?.memberPrice
    if (price != null) {
      return price
    } else {
      return product.salePrice
    }
  }
  return product.salePrice
}

/**
 * 判断两个数字是否相等
 * @param num1 数字1
 * @param num2 数字2
 */
export const isNumberEqual = (num1: number | string, num2: number | string) => {
  const num1Num = typeof num1 === "string" ? parseFloat(num1) : num1
  const num2Num = typeof num2 === "string" ? parseFloat(num2) : num2
  return math.equal(num1Num, num2Num)
}

/**
 * 获取商品的会员折扣信息标签
 * @param row 商品信息
 * @returns 会员折扣信息标签
 */
export const getMemberDiscountInfoLabel = (row: ItemOrderVOExt) => {
  const type = row.memberLevelDiscountInfo?.type
  return type === "memberPrice" ? "会员价" : `会员${convertRateToDiscount(row.memberLevelDiscountInfo?.memberDiscount?.discount ?? 0)}折`
}

/**
 * 获取商品的折扣信息标签
 * @param product 商品信息
 * @param currentMember 当前会员信息
 * @param enabledMemberPrice 是否启用会员价
 * @returns 折扣信息标签
 */
export const getDiscountInfoLabel = (
  product: ProductItemType,
  currentMember: MerchantCustomerUserDetailDTO | null | undefined,
  enabledMemberPrice: number,
) => {
  if (!currentMember) {
    return ""
  }
  const memberDiscountInfo = product.memberDiscountInfo
  if (!memberDiscountInfo) {
    return ""
  }
  if (memberDiscountInfo.type === "memberDiscount") {
    const discount = findMemberDiscount(memberDiscountInfo, currentMember)?.discount
    if (discount && !isNumberEqual(discount, 1)) {
      return `会员${convertRateToDiscount(discount, 2)}折`
    } else {
      return ""
    }
  } else if (memberDiscountInfo.type === "memberPrice" && enabledMemberPrice === 1) {
    return findMemberPrice(memberDiscountInfo, currentMember) ? "会员价" : ""
  }
  return ""
}

export function mergeItems(items: TransformedItemOrderVOTable[]): OrderItem[] {
  const mergedItems: TransformedItemOrderVOTable[] = []

  for (const item of items) {
    const existingItem = mergedItems.find((mergedItem) => {
      if (item.packageSelectionKey || mergedItem.packageSelectionKey) {
        return false
      }
      const flag =
        mergedItem.itemId === item.itemId &&
        mergedItem.packageSelectionKey === item.packageSelectionKey &&
        buildReceiptItemVariantKey(mergedItem) === buildReceiptItemVariantKey(item) &&
        (
          (
            Boolean(item.brandPointRedeemed) &&
            Boolean(mergedItem.brandPointRedeemed)
          ) ||
          (
            Number(item.salePrice) * (item.count ?? 0) === Number(item.itemPayFee) &&
            Number(mergedItem.salePrice) * (mergedItem.count ?? 0) === Number(mergedItem.itemPayFee)
          )
        )
      return flag
    })

    if (existingItem) {
      // 合并商品
      existingItem.count = (existingItem.count ?? 0) + (item.count ?? 0)
      existingItem.itemQuantity += item.itemQuantity ?? 0
      existingItem.itemTotalFee = convertCentsToYuan(
        (Number(existingItem.itemTotalFee) + Number(item.itemTotalFee)) * 100,
      )
      existingItem.itemPayFee = convertCentsToYuan((Number(existingItem.itemPayFee) + Number(item.itemPayFee)) * 100)
      existingItem.salePrice = convertCentsToYuan(Number(item.salePrice) * 100)
      mergePackageChildItems(existingItem, item)

      // 合并折扣信息
      if (item.discountInfo && existingItem.discountInfo) {
        existingItem.discountInfo.discountAmount =
          Number(existingItem.discountInfo.discountAmount ?? 0) + Number(item.discountInfo.discountAmount ?? 0)
      } else if (item.discountInfo) {
        existingItem.discountInfo = { ...item.discountInfo }
      }

      // 保留会员等级折扣信息
      if (!existingItem.memberLevelDiscountInfo && item.memberLevelDiscountInfo) {
        existingItem.memberLevelDiscountInfo = { ...item.memberLevelDiscountInfo }
      }
    } else {
      // 添加新商品
      mergedItems.push({
        ...item,
        salePrice: convertCentsToYuan(Number(item.salePrice) * 100),
        itemTotalFee: convertCentsToYuan(Number(item.itemTotalFee) * 100),
        itemPayFee: convertCentsToYuan(Number(item.itemPayFee) * 100),
      })
    }
  }

  const result = mergedItems.map((item) => {
    const { salePrice: _salePrice, count: _count, packageSelectionKey: _packageSelectionKey, ...rest } = item
    const res: OrderItem = {
      ...rest,
      isConfirm: item.isConfirm?.toString(),
      isSend: item.isSend?.toString(),
      itemId: item.itemId?.toString(),
      itemQuantity: item.itemQuantity?.toString(),
    }
    return res
  })
  return result
}

function mergePackageChildItems(existingItem: TransformedItemOrderVOTable, item: TransformedItemOrderVOTable) {
  if (!item.packageChildItems?.length) {
    return
  }
  if (!existingItem.packageChildItems?.length) {
    existingItem.packageChildItems = item.packageChildItems.map(child => ({ ...child }))
    return
  }

  item.packageChildItems.forEach((child) => {
    const existingChild = existingItem.packageChildItems?.find(
      target => target.itemName === child.itemName,
    )
    if (!existingChild) {
      existingItem.packageChildItems?.push({ ...child })
      return
    }
    const existingCount = getPackageChildCount(existingChild)
    const childCount = getPackageChildCount(child)
    if (existingCount === null || childCount === null) {
      existingChild.count = null
      existingChild.itemQuantity = ""
      return
    }
    const count = existingCount + childCount
    existingChild.count = count
    existingChild.itemQuantity = String(count)
  })
}

function getPackageChildCount(child: PackageChildPrintItem) {
  const rawCount = child.count ?? child.itemQuantity
  if (rawCount === "" || rawCount === null || rawCount === undefined) {
    return null
  }
  const count = Number(rawCount)
  return Number.isFinite(count) ? count : null
}

export function getCashPaymentAmount(priceYuan: number) {
  const amount = priceYuan
  // 向上取整
  const roundedAmount = Math.ceil(amount)
  let result: number[] = [roundedAmount]

  // 基础面值
  const case5 = Math.ceil(roundedAmount / 5) * 5
  const case10 = Math.ceil(roundedAmount / 10) * 10
  const case50 = Math.ceil(roundedAmount / 50) * 50
  const case20 = Math.ceil((roundedAmount - 20) / 50) * 50 + 20
  const case100 = Math.ceil(roundedAmount / 100) * 100
  /**
   * 权重 一百 > 五十 > 十块 > 五块> 二十
   * 默认按权重排序
   */
  result.push(case100, case50, case10, case5, case20)
  /** 删除比整百大的和自己  */
  result = result.filter((item) => item <= case100 && item != amount)
  /** 如果有250,删除270 */
  if (case50 != case100 && case20 >= case50) {
    result = result.filter((item) => item != case20)
  }
  return [...new Set(result)].slice(0, 4).sort((a, b) => a - b)
}
